import { AdvancedSelect } from '../../../widgets/select';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { EmptyList } from '../../../widgets/emptyList';
import { PageHeader } from '../../../widgets/pageHeader';
import { PrimaryButton } from '../../../widgets/buttons';
import { QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { Row, Table, TableHeading } from '../../../widgets/table';
import { SearchInput } from '../../../widgets/input';
import { showErrorNotification, showInfoNotification, showNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { useInfiniteScroll } from '../../../widgets/hooks/useInfiniteScroll';
import { ContainerBase } from '../../../widgets/layouts';
import Stack from '../../../widgets/stack/stack';
import { ZTooltip } from '../../../widgets/ztooltip';
import { debounce } from 'lodash';
import React, { FC, Suspense, useCallback, useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from 'react-relay';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from 'src/components/widgets/loader';
import { Relay, useRelayStore } from 'src/relay';
import { PaletteType } from 'src/shared/enums';
import { Center, DeleteButton, EditButton } from 'src/shared/globals';
import { backofficeUrl, T, toLocalID } from 'src/shared/helpers';
import NotFoundIcon from 'src/shared/material_icons/image/broken_image.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { EyeButton } from '../../placements/placements';
import CreateOrEditPaletteDialog from '../../printType/createOrEditPaletteDialog';
import { ContainerCombo, SearchContainer } from '../../productConfiguration/steps/6-done/connectModelDialog';
import { StyledBadge } from '../../products/productList';
import { colorPaletteSettings_deletePaletteMutation } from './__generated__/colorPaletteSettings_deletePaletteMutation.graphql';
import {
    colorPaletteSettings_palettesWithColors$data,
    colorPaletteSettings_palettesWithColors$key
} from './__generated__/colorPaletteSettings_palettesWithColors.graphql';
import { colorPaletteSettings_palettesWithColorsQuery } from './__generated__/colorPaletteSettings_palettesWithColorsQuery.graphql';
import { InlineHelpLayout } from '../../../widgets/inlineHelpLayout';
import { PaletteColorPreview } from './paletteColorPreview';

const HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/4402564616978-Pantone-CMYK-Mode';

const NameCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 275px;
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
`;

export const IconButtonGrey = styled.div`
    & > * {
        color: #455a64 !important;
        fill: #455a64 !important;
    }
    display: flex;
    justify-content: center;
    align-items: center;
`;

interface ColorPaletteSettingsProps {
    onSavePalette?: () => void;
}

const paletteWithColorsQuery = graphql`
    fragment colorPaletteSettings_palettesWithColors on Query
    @refetchable(queryName: "palettesWithColorsQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 20 }
        after: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: null }
        numberOfColorsToShow: { type: "Int", defaultValue: 6 }
        paletteTypeID: { type: "Int", defaultValue: null }
        orderBy: { type: "String", defaultValue: "DESC" }
    ) {
        palettesWithColors(
            first: $first
            after: $after
            search: $search
            numberOfColorsToShow: $numberOfColorsToShow
            paletteTypeID: $paletteTypeID
            orderBy: $orderBy
        ) @connection(key: "palettesWithColors_palettesWithColors") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    name
                    useConversionTable
                    paletteTypeID
                    isZakekeDefault
                    colors {
                        colorID
                        name
                        rgbHexCode
                        cmykHexCode
                        spotColorName
                    }
                    associationWithPrintTypes
                }
            }
        }
    }
`;

const ColorPaletteSettingsContent: FC<ColorPaletteSettingsProps> = ({ onSavePalette }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const [isFilterPending, startFilterTransition] = useTransition();
    const [, startLoadingMoreTransition] = useTransition();
    const [, startRefetchTransition] = useTransition();
    const [selectedPaletteType, setSelectedPaletteType] = useState<string | null | undefined>('allPalettes');
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const paletteCreated = searchParams.get('palette') === 'created';
    const paletteImported = searchParams.get('palette') === 'imported';
    const paletteUpdated = searchParams.get('palette') === 'updated';

    // Use deferred value for search to prevent unnecessary re-renders during typing
    const deferredSearchFilter = useDeferredValue(searchFilter);

    const query = useLazyLoadQuery<colorPaletteSettings_palettesWithColorsQuery>(
        graphql`
            query colorPaletteSettings_palettesWithColorsQuery(
                $first: Int
                $after: String
                $search: String
                $numberOfColorsToShow: Int
                $paletteTypeID: Int
                $orderBy: String
            ) {
                ...colorPaletteSettings_palettesWithColors
                    @arguments(
                        first: $first
                        after: $after
                        search: $search
                        numberOfColorsToShow: $numberOfColorsToShow
                        paletteTypeID: $paletteTypeID
                        orderBy: $orderBy
                    )
            }
        `,
        {
            first: 20,
            after: '1',
            search: deferredSearchFilter || null,
            numberOfColorsToShow: 6,
            paletteTypeID: selectedPaletteType === 'allPalettes' ? null : selectedPaletteType === 'pantone' ? PaletteType.Pantone : PaletteType.CMYK,
            orderBy: 'DESC'
        },
        { fetchPolicy: 'network-only' }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<
        colorPaletteSettings_palettesWithColorsQuery,
        colorPaletteSettings_palettesWithColors$key
    >(paletteWithColorsQuery, query);

    // Extract palettes data with memoization
    const palettes = useMemo(() => data.palettesWithColors?.edges?.map((edge) => edge.node) || [], [data.palettesWithColors?.edges]);

    // Deletion mutation
    const [deletePalette] = useMutation<colorPaletteSettings_deletePaletteMutation>(graphql`
        mutation colorPaletteSettings_deletePaletteMutation($id: ID!) {
            deletePalette(id: $id) {
                palette {
                    id
                    name
                }
            }
        }
    `);

    const handleDeletePaletteClick = useCallback(
        (paletteID: string) => {
            showDialog(
                'confirm',
                <QuestionDialog
                    title={T._('Delete Palette', 'Admin')}
                    message={T._('Are you sure you want to delete this palette?', 'Admin')}
                    onYesClick={() => {
                        closeDialog('confirm');

                        showInfoNotification({
                            message: T._('Removing...', 'Admin')
                        });

                        deletePalette({
                            variables: {
                                id: paletteID
                            },
                            updater: (store) => {
                                // Simply delete the palette from the store
                                // Relay will automatically clean up the connections
                                store.delete(paletteID);
                            },
                            onCompleted: () => {
                                showSuccessNotification({
                                    message: T._('Palette removed.', 'Admin')
                                });
                            },
                            onError: (error) => {
                                showErrorNotification({
                                    message: T._('An error occurred while deleting the palette.', 'Admin')
                                });
                            }
                        });
                    }}
                />
            );
        },
        [showDialog, closeDialog, deletePalette, invalidate, refetch, selectedPaletteType]
    );

    const handleEditPaletteClick = useCallback(
        (palette: NonNullable<NonNullable<colorPaletteSettings_palettesWithColors$data['palettesWithColors']>['edges']>[number]['node']) => {
            const url = `/settings/color-palette-settings/palette?palette_id=${toLocalID(palette.id)}`;

            navigate(backofficeUrl(url));
        },
        [navigate]
    );

    const handleAddPalette = (shouldShowRadioButtons: boolean, paletteTypeID?: number) => {
        showDialog(
            'create-palette-dialog',
            <CreateOrEditPaletteDialog
                mode='create'
                paletteTypeID={paletteTypeID}
                shouldShowRadioButtons={shouldShowRadioButtons}
                onClosed={(createdPaletteId) => {
                    closeDialog('create-palette-dialog');
                    if (createdPaletteId) {
                        onSavePalette?.();
                    }
                }}
                onPaletteImported={(importedPaletteId) => {
                    closeDialog('create-palette-dialog');
                    onSavePalette?.();
                    showNotification({
                        type: 'success',
                        message: T._('Palette imported and selected successfully.', 'Admin'),
                        dismiss: { duration: 3000 }
                    });
                }}
            />
        );
    };

    const handleEditPalette = (
        palette: NonNullable<NonNullable<colorPaletteSettings_palettesWithColors$data['palettesWithColors']>['edges']>[number]['node']
    ) => {
        if (!palette.id) return;

        showDialog(
            'edit-palette-dialog',
            <CreateOrEditPaletteDialog
                mode='edit'
                editPaletteId={palette.id}
                paletteTypeID={palette.paletteTypeID === PaletteType.Pantone ? 1 : 2}
                onClosed={(editedPaletteId) => {
                    closeDialog('edit-palette-dialog');
                    if (editedPaletteId) {
                        onSavePalette?.();
                    }
                }}
                onPaletteImported={(importedPaletteId) => {
                    closeDialog('edit-palette-dialog');
                    onSavePalette?.();
                    showNotification({
                        type: 'success',
                        message: T._('Palette imported and selected successfully.', 'Admin'),
                        dismiss: { duration: 3000 }
                    });
                }}
            />
        );
    };

    useInfiniteScroll(
        () =>
            startLoadingMoreTransition(() => {
                loadNext(20);
            }),
        hasNext,
        null, // Pass the scroll container element
        isLoadingNext
    );

    useEffect(() => {
        if (paletteCreated || paletteImported || paletteUpdated) {
            startRefetchTransition(() => {
                // Refetch the palettes data
                refetch(
                    {
                        paletteTypeID:
                            selectedPaletteType === 'allPalettes' ? null : selectedPaletteType === 'pantone' ? PaletteType.Pantone : PaletteType.CMYK,
                        search: deferredSearchFilter || null
                    },
                    { fetchPolicy: 'network-only' }
                );

                // Remove the search parameters from the URL after refetch
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('palette');
                setSearchParams(newSearchParams, { replace: true });
            });
        }
    }, [paletteCreated, paletteImported, paletteUpdated, searchParams, setSearchParams]);

    const options = useMemo(() => {
        const paletteTypes: { value: string | null | undefined; label: string }[] = [
            { value: 'pantone', label: 'Pantone' },
            { value: 'cmyk', label: 'CMYK' }
        ];

        return [{ value: 'allPalettes', label: 'Pantone / CMYK' }, ...paletteTypes];
    }, []);

    const setSearchDebounced = useCallback(
        debounce((filter: string) => {
            startFilterTransition(() => {
                setSearchFilter(filter);
            });
        }, 300),
        []
    );

    const handleSearchColor = (search: string) => {
        setSearch(search);
        setSearchDebounced(search);
    };

    return (
        <InlineHelpLayout helpUrl={HELP_URL}>
            {(openHelp) => (
                <ContainerBase>
                    <PageHeader
                        title={T._('Pantone/CMYK Mapping Palettes', 'Admin')}
                        pageKey='pantone-cmyk-palettes'
                        description={T._('Manage your Pantone/CMYK-to-RGB palettes for use in Pantone/CMYK Mode.', 'Admin')}
                        learnMoreLabel={T._('Learn more', 'Admin')}
                        learnMoreUrl={HELP_URL}
                        onLearnMoreClick={openHelp}
                        backLabel={T._('Back', 'Admin')}
                        isBackVisible={true}
                        actions={
                            <>
                                {(palettes.length > 0 || selectedPaletteType !== 'cmyk') && (
                                    // <PrimaryButton $size='medium' onClick={() => navigate(backofficeUrl('/settings/color-palette-settings/palette/'))}>
                                    // 	{T._('Add', 'Admin')}
                                    // </PrimaryButton>
                                    <PrimaryButton $size='medium' onClick={() => handleAddPalette(true)}>
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                        {T._('Add', 'Admin')}
                                    </PrimaryButton>
                                )}
                            </>
                        }
                    />
                    <Stack>
                        <ContainerCombo>
                            <SearchContainer gridTemplateColumns='500px 1fr'>
                                <AdvancedSelect
                                    options={options}
                                    className='comboControl'
                                    value={options.find((s) => s.value === selectedPaletteType)}
                                    placeholder={T._('All library', 'Admin')}
                                    onChange={(item) => {
                                        startFilterTransition(() => {
                                            setSelectedPaletteType(item?.value);
                                        });
                                    }}
                                />
                                <SearchInput
                                    fullWidth
                                    placeholder={T._('Search...', 'Admin')}
                                    onSearch={(value) => {
                                        handleSearchColor(value);
                                    }}
                                    disabled={palettes.length === 0 && searchFilter === ''}
                                />
                            </SearchContainer>
                        </ContainerCombo>
                        {palettes.length > 0 ? (
                            <>
                                <PalettesList
                                    palettes={palettes}
                                    isPending={isFilterPending}
                                    onDeletePalette={handleDeletePaletteClick}
                                    onEditPalette={handleEditPalette}
                                />

                                {hasNext && isLoadingNext && (
                                    <Center style={{ marginTop: '15px' }}>
                                        <Loader />
                                    </Center>
                                )}
                            </>
                        ) : palettes.length === 0 && searchFilter.length > 0 ? (
                            <EmptyList
                                data-testid='empty-list-search-no-results'
                                icon={search.length > 0 ? <NotFoundIcon /> : null}
                                title={T._('No palette found', 'Admin')}
                                subTitle={T._('Sorry, no palette was found for your search. Please try using different keywords.', 'Admin')}
                            />
                        ) : (
                            <EmptyList
                                title={T._("You don't have any CMYK Mapping Palettes", 'Admin')}
                                subTitle={T._('Start by creating one to map CMYK colors to their RGB equivalents and use it in your Printing Method.', 'Admin')}
                                learnMoreLink={HELP_URL}
                                learnMoreText={T._('Learn more', 'Admin')}
                                onLearnMoreClick={openHelp}
                                buttonTitle={T._('Add', 'Admin')}
                                //onButtonClick={() => navigate(backofficeUrl('/settings/color-palette-settings/palette/'))}
                                onButtonClick={() => handleAddPalette(false, PaletteType.CMYK)}
                            />
                        )}
                    </Stack>
                </ContainerBase>
            )}
        </InlineHelpLayout>
    );
};

const PalettesList: FC<{
    palettes: NonNullable<NonNullable<colorPaletteSettings_palettesWithColors$data['palettesWithColors']>['edges']>[number]['node'][];
    isPending?: boolean;
    onDeletePalette: (paletteID: string) => void;
    onEditPalette: (palette: NonNullable<NonNullable<colorPaletteSettings_palettesWithColors$data['palettesWithColors']>['edges']>[number]['node']) => void;
}> = React.memo(({ palettes, isPending, onDeletePalette, onEditPalette }) => {
    const navigate = useNavigate();

    const headings = [
        ['', '0.52fr'],
        ['', '1.02fr'],
        ['', '0.81fr'],
        ['', '0.20fr'],
        ['', '0.71fr']
    ] as TableHeading[];

    const buildRowCells = (palette: NonNullable<NonNullable<colorPaletteSettings_palettesWithColors$data['palettesWithColors']>['edges']>[number]['node']) => {
        // Add null checks to prevent runtime errors
        if (!palette) return [];

        const name: string = palette.name || '';
        const paletteType = T._(PaletteType[palette.paletteTypeID], 'Admin');

        const paletteAssociationWithPrintTypesInfo = () => {
            if (palette.associationWithPrintTypes.length > 0) {
                return (
                    <>
                        <span>{T._('This palette is associated with following print types:', 'Admin')}</span>
                        <br />
                        {palette.associationWithPrintTypes.map((name) => (
                            <>
                                <span key={name}>"{name}"</span>
                                <br />
                            </>
                        ))}
                        <br />
                        <span>{T._('To delete this palette, please first remove its association with the print types listed above.', 'Admin')}</span>
                    </>
                );
            }

            return <span>{T._('Cannot delete palette associated with print types', 'Admin')}</span>;
        };

        return [
            <PaletteColorPreview colors={(palette.colors || []).map((color) => ({ rgbHexCode: color.rgbHexCode }))} maxSlots={6} />,
            <ZTooltip position={'top'} title={name} isOverflowEnabled={true}>
                <NameCell>{name}</NameCell>
            </ZTooltip>,
            <div>{paletteType}</div>,
            <>
                {palette.isZakekeDefault && (
                    <StyledBadge color={theme.app.primary} badgeColor={'#DFE3E8'} showCircle={false}>
                        {T._('Default', 'Admin')}
                    </StyledBadge>
                )}
            </>,
            <ActionsColContainer>
                <IconButtonGrey>
                    {!palette.isZakekeDefault ? (
                        <>
                            <EditButton
                                onClick={() => {
                                    onEditPalette(palette);
                                }}
                            />
                            {palette.associationWithPrintTypes.length > 0 ? (
                                <ZTooltip html={paletteAssociationWithPrintTypesInfo()} addContainer={true} position='left'>
                                    <DeleteButton disabled={true} onClick={() => {}} />
                                </ZTooltip>
                            ) : (
                                <DeleteButton
                                    onClick={() => {
                                        onDeletePalette(palette.id);
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        <EyeButton
                            onClick={() => {
                                onEditPalette(palette);
                            }}
                            enabled={true}
                        />
                    )}
                </IconButtonGrey>
            </ActionsColContainer>
        ];
    };

    return (
        <>
            <Table headings={headings} $grayout={isPending}>
                {palettes
                    .filter((palette) => palette && palette.id)
                    .map((palette, i) => {
                        return <Row key={palette.id || i.toString()} cells={buildRowCells(palette)} />;
                    })}
            </Table>
        </>
    );
});

export const ColorPaletteSettings: FC = () => {
    const [fetchKey, setFetchKey] = useState(0);

    const updateComponent = () => {
        setFetchKey((prev) => prev + 1);
    };

    return (
        <Relay key={fetchKey}>
            <Suspense
                fallback={
                    <Center style={{ height: '100vh' }}>
                        <Loader />
                    </Center>
                }
            >
                <ColorPaletteSettingsContent onSavePalette={updateComponent} />
            </Suspense>
        </Relay>
    );
};
